import React, { useEffect, useState } from 'react';
import apiClient from '../config/axios';
import { useDebounce } from '../hooks/useDebounce';

/**
 * Choosing a handle — issue 500 §1.
 *
 * A handle is PERMANENT, so the one screen this component exists for is the
 * confirm step: the handle shown exactly as it will appear forever, with copy
 * that says plainly it cannot be changed, BEFORE it is written. A typo becomes
 * permanent; this is one screen against a class of problems that otherwise
 * never closes.
 *
 * The check runs as the person types (debounced, against the public check
 * route — under the app limiter, not /api/auth's 10/min) and the refusal
 * reason is shown in words. Never derived from the email, never suggested
 * from it: a handle is public and an email is private (§1).
 *
 * `useHandleCheck` is exported for the register form, which shows the same
 * live preview without the two-step confirm (there is no session to claim
 * with until the account exists — the claim follows registration).
 */

export const HANDLE_HINT = 'Letters, numbers and symbols, 3–30 characters. No spaces. This is how people will find you, and it cannot be changed later.';

export function useHandleCheck(value) {
  const debounced = useDebounce(value, 350);
  const [check, setCheck] = useState({ status: 'idle' });

  useEffect(() => {
    const v = debounced;
    if (!v) { setCheck({ status: 'idle' }); return undefined; }
    let cancelled = false;
    setCheck({ status: 'checking' });
    apiClient.post('/api/users/handle/check', { handle: v })
      .then((res) => {
        if (cancelled) return;
        if (res.data && res.data.ok) setCheck({ status: 'ok', handle: res.data.handle });
        else setCheck({ status: 'bad', reason: res.data && res.data.reason, message: (res.data && res.data.message) || 'That handle cannot be used.' });
      })
      .catch(() => { if (!cancelled) setCheck({ status: 'error' }); });
    return () => { cancelled = true; };
  }, [debounced]);

  // The check is only current when it was made for what is in the box now.
  const current = debounced === value ? check : { status: value ? 'checking' : 'idle' };
  return current;
}

export function HandleFeedback({ check, value }) {
  if (!value) return null;
  if (check.status === 'checking') return <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Checking…</p>;
  if (check.status === 'bad') return <p className="form-error text-xs mt-1" role="alert">{check.message}</p>;
  if (check.status === 'error') return <p className="form-error text-xs mt-1" role="alert">Could not check that right now.</p>;
  if (check.status === 'ok') {
    return (
      <p className="text-xs mt-1" role="status" style={{ color: 'var(--link)' }}>
        <strong>@{check.handle}</strong> is available.
      </p>
    );
  }
  return null;
}

export default function HandleChooser({ onConfirm, idPrefix = 'handle', compact = false }) {
  const [value, setValue] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const check = useHandleCheck(value);
  const ready = check.status === 'ok' && check.handle === value;

  const confirm = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onConfirm(value);
    } catch (err) {
      const data = err && err.response && err.response.data;
      setError((data && data.message) || (data && data.error === 'handle_taken'
        ? 'Somebody claimed that handle just now. Choose another.'
        : 'Could not save your handle. Please try again.'));
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  if (confirming) {
    return (
      <div className="space-y-3" data-testid="handle-confirm">
        <p className="text-primary">
          You will be <strong className="text-lg">@{value}</strong>
        </p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          This is exactly how it will appear, everywhere, for good. A handle cannot be changed
          once it is chosen — check the spelling before you confirm.
        </p>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn btn-primary" onClick={confirm} disabled={saving}>
            {saving ? 'Saving…' : `Confirm @${value}`}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setConfirming(false)} disabled={saving}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={`${idPrefix}-input`} className="form-label">Handle</label>
        <div className="flex items-center gap-1">
          <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>@</span>
          <input
            id={`${idPrefix}-input`}
            type="text"
            className="input flex-1"
            autoComplete="off"
            spellCheck={false}
            maxLength={30}
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(null); }}
            aria-describedby={`${idPrefix}-hint`}
          />
        </div>
        {!compact && (
          <p id={`${idPrefix}-hint`} className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{HANDLE_HINT}</p>
        )}
        <HandleFeedback check={check} value={value} />
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)} disabled={!ready}>
        Continue
      </button>
    </div>
  );
}
